const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Auth token management
let authToken = null;

/**
 * Set the auth token for all requests
 */
export function setAuthToken(token) {
  authToken = token;
}

/**
 * Clear the auth token
 */
export function clearAuthToken() {
  authToken = null;
}

/**
 * Get current auth token
 */
export function getAuthToken() {
  return authToken;
}

// Custom error class for API errors
class ApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

// Request with retry logic for transient failures
async function request(endpoint, options = {}, retries = 2) {
  const url = `${API_URL}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const config = {
    headers,
    credentials: 'include',
    ...options,
  };

  // Merge any additional headers from options
  if (options.headers) {
    config.headers = { ...config.headers, ...options.headers };
  }

  if (config.body && typeof config.body === 'object') {
    config.body = JSON.stringify(config.body);
  }

  try {
    const response = await fetch(url, config);

    // Handle no-content responses
    if (response.status === 204) {
      return null;
    }

    let data;
    try {
      data = await response.json();
    } catch {
      if (!response.ok) {
        throw new ApiError('Server error', response.status, 'SERVER_ERROR');
      }
      return null;
    }

    if (!response.ok) {
      throw new ApiError(
        data.error || data.message || 'Request failed',
        response.status,
        data.code || 'UNKNOWN_ERROR'
      );
    }

    return data;
  } catch (error) {
    // Retry on network errors
    if (error.name === 'TypeError' && retries > 0) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return request(endpoint, options, retries - 1);
    }

    // Re-throw API errors as-is
    if (error instanceof ApiError) {
      throw error;
    }

    // Convert network errors to user-friendly message
    throw new ApiError(
      'Unable to connect to server. Please check your connection.',
      0,
      'NETWORK_ERROR'
    );
  }
}

export const api = {
  // ============================================
  // Authentication
  // ============================================
  register: (data) => request('/api/auth/register', { method: 'POST', body: data }),
  login: (data) => request('/api/auth/login', { method: 'POST', body: data }),
  logout: () => request('/api/auth/logout', { method: 'POST' }),
  getCurrentUser: () => request('/api/auth/me'),
  refreshToken: () => request('/api/auth/refresh', { method: 'POST' }),
  refreshTokenWithRotation: () => request('/api/auth/refresh-token', { method: 'POST' }),
  requestMagicLink: (data) => request('/api/auth/magic-link', { method: 'POST', body: data }),
  verifyMagicLink: (data) => request('/api/auth/verify-magic-link', { method: 'POST', body: data }),
  requestPasswordReset: (data) => request('/api/auth/reset-password', { method: 'POST', body: data }),
  resetPassword: (data) => request('/api/auth/reset-password/confirm', { method: 'POST', body: data }),
  changePassword: (data) => request('/api/auth/change-password', { method: 'POST', body: data }),

  // ============================================
  // Events
  // ============================================
  getEvents: (params = {}) => {
    const query = new URLSearchParams();
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return request(`/api/events${qs ? `?${qs}` : ''}`);
  },
  getEvent: (id) => request(`/api/events/${id}`),
  createEvent: (data) => request('/api/events', { method: 'POST', body: data }),
  updateEvent: (id, data) => request(`/api/events/${id}`, { method: 'PUT', body: data }),

  // Organizers
  getOrganizers: (params = {}) => {
    const query = new URLSearchParams();
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return request(`/api/organizers${qs ? `?${qs}` : ''}`);
  },
  getOrganizer: (id) => request(`/api/organizers/${id}`),
  getPromoterBySlug: (slug) => request(`/api/organizers/slug/${slug}`),
  createOrganizer: (data) => request('/api/organizers/create', { method: 'POST', body: data }),
  updateAnalytics: (id, data) => request(`/api/organizers/${id}/analytics`, { method: 'PUT', body: data }),
  updateOrganizerSettings: (id, data) => request(`/api/organizers/${id}/settings`, { method: 'PUT', body: JSON.stringify(data) }),
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    const url = `${API_URL}/api/organizers/${id}/logo`;
    const headers = {};
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    return fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    }).then(async (res) => {
      const data = await res.json();
      if (!res.ok) throw new ApiError(data.error || 'Upload failed', res.status, data.code);
      return data;
    });
  },
  deleteLogo: (id) => request(`/api/organizers/${id}/logo`, { method: 'DELETE' }),
  getOnboardLink: (id) => request(`/api/organizers/${id}/onboard-link`),
  completeOnboarding: (id) => request(`/api/organizers/${id}/complete-onboarding`, { method: 'POST' }),
  refreshOrganizerStatus: (id) => request(`/api/organizers/${id}/refresh-status`, { method: 'POST' }),

  // Checkout
  createCheckoutSession: (data) => request('/api/checkout/create-session', { method: 'POST', body: data }),
  completePayment: (orderId) => request(`/api/checkout/complete/${orderId}`, { method: 'POST' }),
  getOrder: (id, email) => request(`/api/checkout/order/${id}?email=${encodeURIComponent(email)}`),

  // Seats (for seated events)
  getSeatMap: (eventId) => request(`/api/seats/${eventId}`),
  holdSeats: (eventId, data) => request(`/api/seats/${eventId}/hold`, { method: 'POST', body: data }),

  // Circuits (multi-venue tours)
  getCircuits: () => request('/api/circuits'),
  getCircuit: (idOrSlug) => request(`/api/circuits/${idOrSlug}`),
  createCircuit: (data) => request('/api/circuits', { method: 'POST', body: data }),
  updateCircuit: (id, data) => request(`/api/circuits/${id}`, { method: 'PUT', body: data }),
  deleteCircuit: (id) => request(`/api/circuits/${id}`, { method: 'DELETE' }),
  addEventToCircuit: (circuitId, eventId) => request(`/api/circuits/${circuitId}/events`, { method: 'POST', body: { eventId } }),
  removeEventFromCircuit: (circuitId, eventId) => request(`/api/circuits/${circuitId}/events/${eventId}`, { method: 'DELETE' }),

  // Tickets
  getTickets: (orderId, email) => request(`/api/tickets/${orderId}?email=${encodeURIComponent(email)}`),
  resendTicketEmail: (orderId, email) => request(`/api/tickets/${orderId}/resend-email`, { method: 'POST', body: { email } }),
  validateTicket: (qrPayload) => request('/api/tickets/validate', { method: 'POST', body: { qrPayload } }),

  // Wallet
  getWalletStatus: () => request('/api/tickets/wallet-status'),
  getAppleWalletUrl: (orderId, ticketIndex, email) => `${API_URL}/api/tickets/${orderId}/apple-wallet/${ticketIndex}?email=${encodeURIComponent(email)}`,
  getGoogleWalletUrl: (orderId, ticketIndex, email) => request(`/api/tickets/${orderId}/google-wallet/${ticketIndex}?email=${encodeURIComponent(email)}`),

  // Terminal
  getTerminalLocations: (organizerId) => request(`/api/terminal/locations?organizerId=${organizerId}`),
  createTerminalLocation: (data) => request('/api/terminal/locations', { method: 'POST', body: data }),
  getTerminalConnectionToken: (locationId) => request('/api/terminal/connection-token', { method: 'POST', body: { locationId } }),
  getTerminalReaders: (locationId) => request(`/api/terminal/readers?locationId=${locationId}`),
  registerTerminalReader: (data) => request('/api/terminal/readers/register', { method: 'POST', body: data }),
  deleteTerminalReader: (readerId) => request(`/api/terminal/readers/${readerId}`, { method: 'DELETE' }),
  createSimulatedReader: (locationId) => request('/api/terminal/readers/simulate', { method: 'POST', body: { locationId } }),
  createDoorSale: (data) => request('/api/terminal/door-sale', { method: 'POST', body: data }),
  processTerminalPayment: (readerId, paymentIntentId) => request('/api/terminal/process-payment', { method: 'POST', body: { readerId, paymentIntentId } }),
  cancelTerminalPayment: (readerId) => request('/api/terminal/cancel-payment', { method: 'POST', body: { readerId } }),
  completeDoorSale: (orderId) => request(`/api/terminal/complete-sale/${orderId}`, { method: 'POST' }),

  // ============================================
  // Analytics
  // ============================================
  getAnalyticsOverview: (organizerId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/analytics/${organizerId}/overview${query ? `?${query}` : ''}`);
  },
  getAnalyticsTrends: (organizerId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/analytics/${organizerId}/trends${query ? `?${query}` : ''}`);
  },
  getAnalyticsFunnel: (organizerId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/analytics/${organizerId}/funnel${query ? `?${query}` : ''}`);
  },
  getAnalyticsComparison: (organizerId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/analytics/${organizerId}/comparison${query ? `?${query}` : ''}`);
  },
  getEventPacing: (eventId) => request(`/api/events/${eventId}/pacing`),
  exportAnalytics: (organizerId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/analytics/${organizerId}/export${query ? `?${query}` : ''}`);
  },
};

export { ApiError };
